import {
  IonButton,
  IonIcon,
  IonInput,
  IonRouterLink,
  IonText
} from '@ionic/react';
import React, { KeyboardEvent, useEffect, useState } from 'react';

import { ButtonContainer, Spacer } from '@/components/common';
import { useLanguage } from '@/contexts/LanguageContext';
import { useScheduleFlow } from '@/contexts/ScheduleFlowContext';
import { useToast } from '@/hooks';
import {
  OTP_EXPIRY_IN_SECONDS,
  OTP_LENGTH,
  SCHEDULE_FLOW_STEPS
} from '@/utils/constants';
import { arrowForwardCircleOutline } from 'ionicons/icons';

import './Verification.css';

const Verification: React.FC = () => {
  const { t } = useLanguage();
  const { mobileNumber, resendOtp, verifyOtp, setCurrentStep } =
    useScheduleFlow();
  const displayToast = useToast();

  const [otp, setOtp] = useState<string>('');
  const [hasError, setHasError] = useState<boolean>(false);
  const [timer, setTimer] = useState<number>(OTP_EXPIRY_IN_SECONDS);
  const [isResendDisabled, setIsResendDisabled] = useState<boolean>(true);

  const copy = {
    subHeader: t('schedule.verification.subHeader'),
    header: (
      <>
        {t('schedule.verification.header.message')}{' '}
        <IonText color="primary">
          {t('schedule.verification.header.code')}
        </IonText>{' '}
        {t('schedule.verification.header.sentTo')}
      </>
    ),
    buttonText: t('schedule.verification.buttonText'),
    changeNumberText: t('schedule.verification.changeNumberText'),
    verificationCodeLabel: t('schedule.verification.verificationCodeLabel'),
    verificationCodePlaceholder: t(
      'schedule.verification.verificationCodePlaceholder'
    ),
    resendPrompt: t('schedule.verification.resendPrompt'),
    resendText: t('schedule.verification.resendText'),
    resendWithTimer: t('schedule.verification.resendWithTimer', {
      seconds: timer
    })
  };

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setIsResendDisabled(false);
    }
  }, [timer]);

  const onInput = (event: CustomEvent) => {
    setOtp(event.detail.value!);
    setHasError(false);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      onNext();
    }
  };

  const onNext = () => {
    if (otp.length === OTP_LENGTH) {
      setHasError(false);
      verifyOtp(otp);
    } else {
      setHasError(true);
      displayToast({
        message: t('schedule.verification.errorMessage'),
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
    }
  };

  const onResend = () => {
    if (!isResendDisabled) {
      resendOtp();
      setTimer(OTP_EXPIRY_IN_SECONDS);
      setIsResendDisabled(true);
    }
  };

  const formattedMobileNumber = mobileNumber
    ? `0${mobileNumber.slice(0, 3)} ${mobileNumber.slice(3, 6)} ${mobileNumber.slice(6, 10)}`
    : '--';

  return (
    <div className="verification">
      <Spacer top={32} bottom={16}>
        <h4 className="verification__sub-header">{copy.subHeader}</h4>
        <h1 className="verification__header">{copy.header}</h1>
      </Spacer>
      <div className="verification__number">
        <h2 className="verification__mobile-number">{formattedMobileNumber}</h2>
        <IonRouterLink
          onClick={() => setCurrentStep(SCHEDULE_FLOW_STEPS.SIGN_UP)}
          className="verification__change-number"
        >
          {copy.changeNumberText}
        </IonRouterLink>
      </div>

      <Spacer top={16} bottom={102}>
        <div className="verification__form">
          <div className={`input ${hasError ? 'error' : ''}`}>
            <IonInput
              type="text"
              inputmode="numeric"
              label={copy.verificationCodeLabel}
              labelPlacement="stacked"
              value={otp}
              placeholder={copy.verificationCodePlaceholder}
              onIonInput={onInput}
              onKeyDown={onKeyDown}
              maxlength={6}
            />
          </div>
          <p className="verification__resend-otp">
            {copy.resendPrompt}{' '}
            <IonRouterLink
              onClick={onResend}
              className={isResendDisabled ? 'disabled' : ''}
              color={isResendDisabled ? 'medium' : 'primary'}
            >
              {isResendDisabled
                ? t('schedule.verification.resendWithTimer', { seconds: timer })
                : copy.resendText}
            </IonRouterLink>
          </p>
        </div>
      </Spacer>
      <ButtonContainer>
        <IonButton
          className="verification__next-button"
          onClick={onNext}
          expand="block"
          fill="solid"
          size="default"
          color="primary"
          strong
        >
          <IonIcon slot="start" icon={arrowForwardCircleOutline}></IonIcon>
          {copy.buttonText}
        </IonButton>
      </ButtonContainer>
    </div>
  );
};

export default Verification;
